import './index.css'
import * as React from 'react'
import { inputColors } from '@disco/utils/colors'
import ColorPicker from './color-picker'
type Props = {
  name: string
  value: string
  onChange: (color: Record<string, any>) => any
  boundsRef?: {
    current: null | HTMLDivElement
  }
  portal?: boolean
  colors?: readonly string[]
}

/**
 * A color input component that displays a color picker on hover with input field
 * @param name - The name attribute for the input field
 * @param value - The current color value (hex color string)
 * @param onChange - Callback function called when color changes, receives color object with hex property
 * @param boundsRef - Optional ref to container element for positioning the color picker
 * @param portal - Whether to render the color picker in a portal - defaults to false
 * @param colors - Swatches offered by the picker - defaults to `inputColors`
 * @returns A div element containing color sample, input field, and color picker
 */
class ColorInput extends React.Component<
  Props,
  {
    showPicker: boolean
  }
> {
  _input: HTMLDivElement | null | undefined = null
  _delay: ReturnType<typeof setTimeout> | null = null
  _onEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange({
      hex: e.target.value,
    })
  }
  state: {
    showPicker: boolean
  } = {
    showPicker: false,
  }

  componentWillUnmount() {
    this._clearDelay()
  }

  _onMouseEnter: () => void = () => {
    this._delay = setTimeout(this._showPicker, 500)
  }
  _onMouseLeave: () => void = () => {
    this._clearDelay()

    this._hidePicker()
  }
  _clearDelay: () => void = () => {
    if (this._delay) {
      clearTimeout(this._delay)
    }
  }
  _showPicker: () => void = () =>
    this.setState({
      showPicker: true,
    })
  _hidePicker: () => void = () =>
    this.setState({
      showPicker: false,
    })

  _positionColorpicker(): null | {
    left: number
    top: number
    triangle: string
  } {
    if (!this._input) return null
    let bottomConstrant

    if (this.props.boundsRef && this.props.boundsRef.current) {
      const bounds = this.props.boundsRef.current.getBoundingClientRect()
      bottomConstrant = bounds.bottom || bounds.top + 600
    } else {
      bottomConstrant = window.innerHeight
    }

    const inputDimensions = this._input.getBoundingClientRect()

    const colorPickerHeight = 132 // hardcoded

    let top = inputDimensions.top + inputDimensions.height
    let triangle = 'top-left'

    if (top + colorPickerHeight > bottomConstrant) {
      triangle = 'hide'
      top = inputDimensions.top - colorPickerHeight
    }

    return {
      triangle: triangle,
      top: top,
      left: inputDimensions.left,
    }
  }

  render(): React.ReactElement<'div'> {
    const { name, value, onChange, portal, colors = inputColors } = this.props
    const appNode = document.getElementById('app')

    const anchor = this._positionColorpicker()

    const picker = (
      <ColorPicker
        color={value || colors[0]}
        onChange={onChange}
        colors={colors}
        triangle={anchor ? anchor.triangle : 'top-left'}
      />
    )
    const component = (
      <div className="color-picker__colors-dialog">{picker}</div>
    )
    return (
      <div
        className="color-picker"
        onMouseEnter={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        ref={r => {
          this._input = r
        }}
      >
        <div className="color-picker__color-box">
          <div
            className="color-picker__color-sample"
            style={{
              background: value || colors[0],
            }}
          />
        </div>
        <input
          autoComplete="off" // `autoComplete="off"` may not work in some browsers
          className="color-picker__padded-input"
          value={value || colors[0]}
          name={name}
          maxLength={7}
          onChange={this._onEdit}
        />
        {this.state.showPicker && component}
      </div>
    )
  }
}

export default ColorInput
